/**
 * Tenant Dashboard Page
 * =====================
 * لوحة المعلومات الرئيسية للشركة
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTenantAuth } from '../../context/TenantAuthContext';
import { useToast } from '../../context/ToastContext';
import './DashboardPage.css';
import SAR from '../../../components/SAR';

// Inline SVG Icons
const I = {
  orders: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1"/></svg>,
  revenue: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>,
  drivers: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="3"/></svg>,
  trucks: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 16H9m10 0h3v-3.15a1 1 0 0 0-.84-.99L16 11l-2.7-3.6a1 1 0 0 0-.8-.4H5.24a2 2 0 0 0-1.8 1.1l-.8 1.63A6 6 0 0 0 2 12.42V16h2"/><circle cx="6.5" cy="16.5" r="2.5"/><circle cx="16.5" cy="16.5" r="2.5"/></svg>,
  ships: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 20h20M4 20V10l8-6 8 6v10"/></svg>,
  employees: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>,
  support: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>,
  trending: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>,
  clock: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  warning: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
  check: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>,
  refresh: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>,
};

const StatCard = ({ icon, label, value, change, changeType, color }) => (
  <div className={`dashboard-stat dashboard-stat--${color}`}>
    <div className="dashboard-stat__icon">{icon}</div>
    <div className="dashboard-stat__content">
      <span className="dashboard-stat__value">{value}</span>
      <span className="dashboard-stat__label">{label}</span>
    </div>
    {change && (
      <div className={`dashboard-stat__change dashboard-stat__change--${changeType}`}>
        {I.trending}
        <span>{change}</span>
      </div>
    )}
  </div>
);

const OrderRow = ({ order }) => {
  const statusColors = {
    pending: 'warning',
    assigned: 'info',
    in_progress: 'primary',
    completed: 'success',
    cancelled: 'danger'
  };
  
  const statusLabels = {
    pending: 'قيد الانتظار',
    assigned: 'تم التعيين',
    in_progress: 'جاري التنفيذ',
    completed: 'مكتمل',
    cancelled: 'ملغي'
  };

  return (
    <div className="dashboard-order">
      <div className="dashboard-order__info">
        <span className="dashboard-order__number">{order.order_number}</span>
        <span className="dashboard-order__customer">{order.customer_name}</span>
      </div>
      <div className="dashboard-order__amount">{order.total_amount?.toFixed(2)}{' '}<SAR className="h-[0.8em] w-auto" /></div>
      <span className={`dashboard-badge dashboard-badge--${statusColors[order.status]}`}>
        {statusLabels[order.status] || order.status}
      </span>
    </div>
  );
};

const AlertCard = ({ alert }) => {
  const icons = { warning: I.warning, error: I.warning, info: I.support };
  
  return (
    <div className={`dashboard-alert dashboard-alert--${alert.type}`}>
      <div className="dashboard-alert__icon">{icons[alert.type]}</div>
      <div className="dashboard-alert__content">
        <span className="dashboard-alert__title">{alert.title}</span>
        <span className="dashboard-alert__message">{alert.message}</span>
      </div>
    </div>
  );
};

const DashboardPage = () => {
  const { apiCall, tenant } = useTenantAuth();
  const { error: showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [alerts, setAlerts] = useState([]);

  const loadDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const [summaryData, ordersData, alertsData] = await Promise.all([
        apiCall('/tenant/dashboard/summary'),
        apiCall('/tenant/dashboard/recent-orders?limit=5'),
        apiCall('/tenant/dashboard/alerts')
      ]);
      
      setSummary(summaryData);
      setRecentOrders(ordersData);
      setAlerts(alertsData);
    } catch (err) {
      showError('فشل تحميل بيانات لوحة المعلومات');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [apiCall, showError]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="dashboard-loading__spinner" />
        <span>جاري تحميل البيانات...</span>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <div className="dashboard-header">
        <div className="dashboard-header__welcome">
          <h1>مرحباً بك في {tenant?.name}</h1>
          <p>نظرة عامة على أداء شركتك</p>
        </div>
        <button className="dashboard-header__refresh" onClick={loadDashboardData}>
          {I.refresh}
          <span>تحديث</span>
        </button>
      </div>

      {/* Stats Grid */}
      <div className="dashboard-stats">
        <StatCard
          icon={I.orders}
          label="متوسط قيمة الطلب" 
          value={<>{(summary?.orders?.avg_value || 0).toFixed(0)}{' '}<SAR className="h-[0.8em] w-auto" /></>}
          change="+12%"
          changeType="up"
          color="primary"
        />
        <StatCard
          icon={I.revenue}
          label="الإيرادات" 
          value={<>{(summary?.revenue?.total || 0).toLocaleString()}{' '}<SAR className="h-[0.8em] w-auto" /></>}
          change="+8%"
          changeType="up"
          color="success"
        />
        <StatCard
          icon={I.drivers}
          label="السواقين النشطين"
          value={summary?.resources?.active_drivers || 0}
          color="info"
        />
        <StatCard
          icon={I.trucks}
          label="المركبات النشطة"
          value={summary?.resources?.active_vehicles || 0}
          color="warning"
        />
        <StatCard
          icon={I.ships}
          label="الأشياب النشطة"
          value={summary?.resources?.active_ships || 0}
          color="purple"
        />
        <StatCard
          icon={I.support}
          label="تذاكر مفتوحة"
          value={summary?.support?.open_tickets || 0}
          color="danger"
        />
      </div>

      {/* Orders Summary */}
      <div className="dashboard-grid">
        <div className="dashboard-card">
          <div className="dashboard-card__header">
            <h3>حالة الطلبات</h3>
            <span className="dashboard-card__subtitle">اليوم</span>
          </div>
          <div className="dashboard-card__body">
            <div className="dashboard-orders-summary">
              <div className="dashboard-orders-summary__item">
                <span className="dashboard-orders-summary__dot dashboard-orders-summary__dot--warning" />
                <span className="dashboard-orders-summary__label">قيد الانتظار</span>
                <span className="dashboard-orders-summary__value">{summary?.orders?.pending || 0}</span>
              </div>
              <div className="dashboard-orders-summary__item">
                <span className="dashboard-orders-summary__dot dashboard-orders-summary__dot--primary" />
                <span className="dashboard-orders-summary__label">جاري التنفيذ</span>
                <span className="dashboard-orders-summary__value">{summary?.orders?.in_progress || 0}</span>
              </div>
              <div className="dashboard-orders-summary__item">
                <span className="dashboard-orders-summary__dot dashboard-orders-summary__dot--success" />
                <span className="dashboard-orders-summary__label">مكتمل اليوم</span>
                <span className="dashboard-orders-summary__value">{summary?.orders?.completed_today || 0}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="dashboard-card">
          <div className="dashboard-card__header">
            <h3>آخر الطلبات</h3>
            <a href="/tenant/orders" className="dashboard-card__link">عرض الكل</a>
          </div>
          <div className="dashboard-card__body dashboard-card__body--no-padding">
            {recentOrders.length > 0 ? (
              <div className="dashboard-orders-list">
                {recentOrders.map(order => (
                  <OrderRow key={order.id} order={order} />
                ))}
              </div>
            ) : (
              <div className="dashboard-empty">لا توجد طلبات حديثة</div>
            )}
          </div>
        </div>
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="dashboard-card">
          <div className="dashboard-card__header">
            <h3>التنبيهات والإشعارات</h3>
          </div>
          <div className="dashboard-card__body">
            <div className="dashboard-alerts">
              {alerts.map((alert, index) => (
                <AlertCard key={index} alert={alert} />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
